import type { Act, CastAssignment, Character, Personnel, ScheduleEventType } from '../../types';
import type { RehearsalScene } from './types';

const TEMPLATE_REHEARSAL_EVENT_TYPES = new Set<ScheduleEventType>([
    'scene',
    'act',
    'rehearsal-scene',
    'run',
    'tech',
    'performance',
]);

const PRODUCTION_ATTENDANCE_PRIMARY_ROLES = new Set([
    'director',
    'stage manager',
    'assistant stage manager',
]);

const PRODUCTION_ATTENDANCE_TITLES = new Set([
    'director',
    'stage manager',
    'sm',
    'assistant stage manager',
    'asm',
]);

function normalizeProductionRoleLabel(value?: string): string {
    return value?.trim().toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ') ?? '';
}

export function isTemplateRehearsalEventType(eventType: string): eventType is ScheduleEventType {
    return TEMPLATE_REHEARSAL_EVENT_TYPES.has(eventType as ScheduleEventType);
}

export function isProductionAttendancePerson(person: Personnel): boolean {
    const primaryRole = normalizeProductionRoleLabel(person.primaryRole);
    const title = normalizeProductionRoleLabel(person.title);

    return !person.deleted && (
        PRODUCTION_ATTENDANCE_PRIMARY_ROLES.has(primaryRole)
        || PRODUCTION_ATTENDANCE_TITLES.has(title)
        || person.department === 'stage-management'
    );
}

export function getProductionAttendancePersonnel(personnel: Personnel[]): Personnel[] {
    return personnel
        .filter(isProductionAttendancePerson)
        .toSorted((a, b) => a.name.localeCompare(b.name));
}

function addDeduped(ids: string[], seen: Set<string>, id: string): void {
    if (seen.has(id)) {
        return;
    }

    seen.add(id);
    ids.push(id);
}

function isCompanyCallEventType(eventType: string): boolean {
    return eventType === 'run' || eventType === 'tech' || eventType === 'performance';
}

function getSceneIdsForTemplateRehearsal(
    eventType: string,
    targetId: string | null,
    acts: Pick<Act, 'id' | 'sceneIds'>[],
    rehearsalScenes: Pick<RehearsalScene, 'id' | 'relatedSceneIds'>[],
    scenes: { id: string; deleted?: boolean }[]
): string[] {
    switch (eventType) {
        case 'scene':
            return targetId ? [targetId] : [];
        case 'act':
            return acts.find((act) => act.id === targetId)?.sceneIds ?? [];
        case 'rehearsal-scene':
            return rehearsalScenes.find((scene) => scene.id === targetId)?.relatedSceneIds ?? [];
        case 'run':
        case 'tech':
        case 'performance':
            return scenes.filter((scene) => !scene.deleted).map((scene) => scene.id);
        default:
            return [];
    }
}

function getEffectiveCharacterSceneIds(character: Character, activeCharactersById: Map<string, Character>): string[] {
    const sceneIds = [...character.sceneIds];
    const parent = character.parentId ? activeCharactersById.get(character.parentId) : null;

    if (parent) {
        sceneIds.push(...parent.sceneIds);
    }

    return sceneIds;
}

function getCharacterIdsForTemplateRehearsal(
    eventType: string,
    targetId: string | null,
    characters: Character[],
    acts: Pick<Act, 'id' | 'sceneIds'>[],
    rehearsalScenes: Pick<RehearsalScene, 'id' | 'characterIds' | 'relatedSceneIds'>[],
    scenes: { id: string; deleted?: boolean }[]
): Set<string> {
    const characterIds = new Set<string>();
    const rehearsalScene = eventType === 'rehearsal-scene' && targetId
        ? rehearsalScenes.find((scene) => scene.id === targetId)
        : null;
    const activeCharacters = characters.filter((character) => !character.deleted);
    const activeCharactersById = new Map(activeCharacters.map((character) => [character.id, character]));
    const activeCharacterIds = new Set(
        activeCharacters.map((character) => character.id)
    );

    for (const characterId of rehearsalScene?.characterIds ?? []) {
        if (activeCharacterIds.has(characterId)) {
            characterIds.add(characterId);
        }
    }

    const sceneIds = getSceneIdsForTemplateRehearsal(eventType, targetId, acts, rehearsalScenes, scenes);
    if (sceneIds.length === 0) {
        return characterIds;
    }

    const sceneIdSet = new Set(sceneIds);
    for (const character of activeCharacters) {
        if (getEffectiveCharacterSceneIds(character, activeCharactersById).some((sceneId) => sceneIdSet.has(sceneId))) {
            characterIds.add(character.id);
        }
    }

    return characterIds;
}

interface TemplateRehearsalAttendeeOptions {
    eventType: string;
    targetId: string | null;
    characters: Character[];
    acts: Pick<Act, 'id' | 'sceneIds'>[];
    rehearsalScenes: Pick<RehearsalScene, 'id' | 'characterIds' | 'relatedSceneIds'>[];
    scenes: { id: string; deleted?: boolean }[];
    castAssignments: CastAssignment[];
    personnel: Personnel[];
}

export function getDefaultTemplateRehearsalAttendeeIds({
    eventType,
    targetId,
    characters,
    acts,
    rehearsalScenes,
    scenes,
    castAssignments,
    personnel,
}: TemplateRehearsalAttendeeOptions): string[] {
    if (!isTemplateRehearsalEventType(eventType)) {
        return [];
    }

    const attendeeIds: string[] = [];
    const seen = new Set<string>();
    const relevantCharacterIds = getCharacterIdsForTemplateRehearsal(
        eventType,
        targetId,
        characters,
        acts,
        rehearsalScenes,
        scenes
    );

    if (isCompanyCallEventType(eventType)) {
        for (const person of personnel) {
            if (!person.deleted && person.category === 'cast') {
                addDeduped(attendeeIds, seen, person.id);
            }
        }
    } else {
        for (const assignment of castAssignments) {
            if (assignment.role === 'primary' && relevantCharacterIds.has(assignment.characterId)) {
                addDeduped(attendeeIds, seen, assignment.personnelId);
            }
        }
    }

    for (const person of getProductionAttendancePersonnel(personnel)) {
        addDeduped(attendeeIds, seen, person.id);
    }

    return attendeeIds;
}
